/**
 * FavoriteButton — estrella de la barra superior.
 *
 * Marca la entrada de menú de la página actual como acceso directo. Un solo
 * punto para toda la app: funciona en listados, fichas y vistas de diseño sin
 * tener que añadir un botón a cada pantalla.
 *
 * El ámbito se elige en el desplegable (esta empresa / todas mis empresas),
 * porque un mismo acceso puede interesar solo aquí o en todo lo que lleva el
 * usuario.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { findMenuItem, resolveMenuKeyForPath } from '../menuConfig';
import './FavoriteButton.css';

function FavoriteButton({
  favorites = { global: [], company: [] },
  onAdd,
  onRemove,
  saving = false,
  enabled = true,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const menuKey = useMemo(
    () => resolveMenuKeyForPath(location?.pathname),
    [location?.pathname]
  );
  const item = useMemo(() => (menuKey ? findMenuItem(menuKey) : null), [menuKey]);

  const globalFavorite = useMemo(
    () => (favorites.global || []).find((row) => row.menu_key === menuKey),
    [favorites.global, menuKey]
  );
  const companyFavorite = useMemo(
    () => (favorites.company || []).find((row) => row.menu_key === menuKey),
    [favorites.company, menuKey]
  );

  // Cerrar al cambiar de página: el desplegable es de la página anterior.
  useEffect(() => setOpen(false), [location?.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Fuera de una entrada del menú (perfil, editor de documentos…) no hay nada
  // que favoritizar; es mejor no pintar el botón que pintarlo inútil.
  if (!item) return null;

  const isFavorite = Boolean(globalFavorite || companyFavorite);
  const label = t(`menu.items.${item.key}`, item.label);

  const handleAdd = (scope) => {
    setOpen(false);
    onAdd?.(scope, item);
  };
  const handleRemove = (favorite) => {
    setOpen(false);
    onRemove?.(favorite);
  };

  return (
    <div className="navbar-fav" ref={rootRef}>
      <button
        type="button"
        className={`navbar-action-btn navbar-fav-btn ${isFavorite ? 'is-favorite' : ''} ${open ? 'active' : ''}`}
        onClick={() => setOpen((value) => !value)}
        title={
          isFavorite
            ? t('menu.favorites.manageFor', {
                defaultValue: '«{{name}}» está en favoritos',
                name: label,
              })
            : t('menu.favorites.addFor', {
                defaultValue: 'Añadir «{{name}}» a favoritos',
                name: label,
              })
        }
        aria-label={t('menu.favorites.button', 'Favoritos')}
        aria-expanded={open}
        disabled={saving || !enabled}
      >
        {isFavorite ? (
          <StarIconSolid className="navbar-fav-icon" />
        ) : (
          <StarIconOutline className="navbar-fav-icon" />
        )}
        <span className="action-label">{t('menu.favorites.short', 'Favorito')}</span>
      </button>

      {open && (
        <div className="navbar-fav-popover" role="menu">
          <div className="navbar-fav-head">
            <span className="navbar-fav-head-title">{label}</span>
            <span className="navbar-fav-head-sub">
              {t('menu.favorites.headSub', 'Acceso directo en el menú lateral')}
            </span>
          </div>

          {companyFavorite ? (
            <button
              type="button"
              className="navbar-fav-option is-danger"
              role="menuitem"
              onClick={() => handleRemove(companyFavorite)}
            >
              {t('menu.favorites.removeCompany', 'Quitar de esta empresa')}
            </button>
          ) : (
            <button
              type="button"
              className="navbar-fav-option"
              role="menuitem"
              onClick={() => handleAdd('company')}
            >
              {t('menu.favorites.addCompany', 'Añadir solo a esta empresa')}
            </button>
          )}

          {globalFavorite ? (
            <button
              type="button"
              className="navbar-fav-option is-danger"
              role="menuitem"
              onClick={() => handleRemove(globalFavorite)}
            >
              {t('menu.favorites.removeGlobal', 'Quitar de todas mis empresas')}
            </button>
          ) : (
            <button
              type="button"
              className="navbar-fav-option"
              role="menuitem"
              onClick={() => handleAdd('global')}
            >
              {t('menu.favorites.addGlobal', 'Añadir a todas mis empresas')}
            </button>
          )}

          <div className="navbar-fav-foot">
            {t('menu.favorites.footHint', 'Se ordenan y editan en «Personalizar menú».')}
          </div>
        </div>
      )}
    </div>
  );
}

export default FavoriteButton;
